import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowLeft, FileText } from "lucide-react";
import client from "@/lib/api";
import Seo from "@/components/Seo";
import StatusBadge from "@/components/StatusBadge";
import TapInButton from "@/components/TapInButton";
import StrainCard from "@/components/StrainCard";
import { FadeUp } from "@/components/Reveal";

export default function StrainDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    setActive(0);
    window.scrollTo(0, 0);
    client
      .get(`/strains/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const strain = data?.strain;
  const images = strain?.images?.length ? strain.images : [];

  const move = useCallback(
    (dir) => setActive((i) => (images.length ? (i + dir + images.length) % images.length : 0)),
    [images.length]
  );

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") move(1);
      if (e.key === "ArrowLeft") move(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, move]);

  if (notFound)
    return (
      <div className="mx-auto max-w-[1600px] px-5 py-40 text-center md:px-10">
        <h1 className="font-display text-6xl">STRAIN NOT FOUND</h1>
        <Link to="/strains" className="mt-6 inline-block text-yaza-accent">← Back to strains</Link>
      </div>
    );

  if (!strain)
    return <div className="px-5 py-40 text-center text-yaza-dim">Loading…</div>;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: strain.name,
    description: strain.meta_description || strain.name,
    brand: { "@type": "Brand", name: "YAZA EXOTICS" },
    image: images,
  };

  return (
    <>
      <Seo
        title={strain.meta_title || `${strain.name} — YAZA EXOTICS`}
        description={strain.meta_description || `${strain.name} — ${strain.lineage}`}
        image={images[0]}
        jsonLd={jsonLd}
      />

      <div className="mx-auto max-w-[1600px] px-5 pt-10 md:px-10 md:pt-16">
        <Link to="/strains" data-testid="back-to-strains" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-yaza-dim hover:text-yaza-accent">
          <ArrowLeft size={14} /> All Strains
        </Link>
      </div>

      <section className="mx-auto grid max-w-[1600px] gap-10 px-5 py-10 md:grid-cols-2 md:gap-16 md:px-10 md:py-16">
        {/* Gallery */}
        <div data-testid="strain-gallery">
          <div
            className="evidence-frame relative aspect-[4/5] cursor-zoom-in overflow-hidden bg-yaza-surface2"
            onClick={() => images.length && setLightbox(true)}
          >
            {images[active] && (
              <motion.img
                key={active}
                initial={{ opacity: 0.4 }}
                animate={{ opacity: 1 }}
                src={images[active]}
                alt={strain.name}
                className="h-full w-full object-cover"
              />
            )}
            <div className="absolute left-4 top-4">
              <StatusBadge status={strain.status} />
            </div>
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  data-testid={`thumb-${i}`}
                  className={`h-20 w-20 overflow-hidden border ${active === i ? "border-yaza-accent" : "border-yaza-border"}`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <FadeUp>
            <p className="text-xs uppercase tracking-[0.3em] text-yaza-accent">{strain.type}</p>
            <h1 className="mt-3 font-display text-6xl leading-[0.9] md:text-7xl">{strain.name}</h1>
            {strain.lineage && (
              <p className="mt-4 text-lg text-yaza-dim">
                <span className="font-mono text-xs uppercase tracking-widest text-yaza-accent">Lineage · </span>{strain.lineage}
              </p>
            )}
          </FadeUp>

          {/* CASE FILE data block */}
          <div className="mt-10 border-t-2 border-yaza-accent pt-6">
            <p className="mb-6 font-mono text-xs font-bold uppercase tracking-[0.3em] text-yaza-accent">// Case File</p>
            <div className="grid gap-6">
              <div>
                <p className="mb-1 font-mono text-[11px] uppercase tracking-[0.25em] text-yaza-accent">Type</p>
                <p className="font-mono text-sm uppercase tracking-widest text-white">{strain.type}</p>
              </div>

              {strain.terpenes?.length > 0 && (
                <div>
                  <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-yaza-accent">Terpene Profile</p>
                  <div className="flex flex-wrap gap-2">
                    {strain.terpenes.map((t) => (
                      <span key={t} className="border border-yaza-border px-3 py-1 font-mono text-xs uppercase tracking-wider text-white">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {strain.flavor_notes?.length > 0 && (
                <div>
                  <p className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-yaza-accent">Flavor Notes</p>
                  <div className="flex flex-wrap gap-2">
                    {strain.flavor_notes.map((t) => (
                      <span key={t} className="bg-yaza-accent px-3 py-1 font-mono text-xs font-bold uppercase tracking-wider text-black">{t}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {strain.description && (
            <div className="rich mt-8 max-w-prose" dangerouslySetInnerHTML={{ __html: strain.description }} />
          )}

          {strain.coa_url && (
            <a href={strain.coa_url} target="_blank" rel="noreferrer" data-testid="coa-link" className="mt-6 inline-flex items-center gap-2 text-sm text-yaza-accent underline">
              <FileText size={16} /> View COA / Lab Results
            </a>
          )}

          <div className="mt-10">
            <TapInButton context={`${strain.name}`} testid="detail-tapin" />
          </div>
        </div>
      </section>

      {/* Related */}
      {data.related?.length > 0 && (
        <section className="mx-auto max-w-[1600px] border-t border-yaza-border px-5 py-16 md:px-10">
          <h2 className="mb-8 font-display text-4xl md:text-5xl">RELATED GENETICS</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.related.map((s, i) => (
              <StrainCard key={s.id} strain={s} index={i} />
            ))}
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-6"
            data-testid="lightbox"
            onClick={() => setLightbox(false)}
          >
            <button className="absolute right-6 top-6 text-white" onClick={() => setLightbox(false)} data-testid="lightbox-close"><X size={30} /></button>
            {images.length > 1 && (
              <>
                <button className="absolute left-4 text-white md:left-10" onClick={(e) => { e.stopPropagation(); move(-1); }}><ChevronLeft size={40} /></button>
                <button className="absolute right-4 text-white md:right-10" onClick={(e) => { e.stopPropagation(); move(1); }}><ChevronRight size={40} /></button>
              </>
            )}
            <img src={images[active]} alt={strain.name} className="max-h-[85vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
